import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { PlanUnit } from '../../types';

import { apiToFormAdapter, formToApiAdapter } from './utils/utils';

import { createCostCenter, createCostCenterDetail } from 'RestAPIs/admin';

import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';

import { showSnackbar } from 'components/shared';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import SubEntitySection from './create/SubEntitySection';
import {
  CategoryField,
  ColorField,
  PrivilegedEmailField,
  RequestExcludeField,
  SortOrderField,
  TypeField
} from './fields/BaseFields';
import { createPlanUnitSchema } from './planUnitSchema';

const CreatePlanUnit: React.FC<
  FormComponentProps<PlanUnit> & { onCreated?: (brId: number) => void }
> = ({ rowData, handleClose, onCreated }) => {
  const queryClient = useQueryClient();

  const { t } = useTranslation('common');

  const methods = useForm({
    defaultValues: createPlanUnitSchema.cast(apiToFormAdapter(rowData)),
    resolver: yupResolver(createPlanUnitSchema),
    mode: 'onChange'
  });

  const { handleSubmit } = methods;

  const onSubmit = async (payload) => {
    const dataToSave = formToApiAdapter(payload);

    try {
      const { data: response } = await createCostCenter(dataToSave);
      const detailToSave = { ...dataToSave.details, brId: response.brId };

      await createCostCenterDetail(detailToSave);

      onCreated?.(response.brId);

      await queryClient.invalidateQueries({
        queryKey: ['hierarchy', 'list']
      });
    } catch {
      showSnackbar({
        severity: 'error',
        message: t('somethingWentWrong')
      });
    }

    handleClose();
  };

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <Box maxWidth={360}>
        <MultiStepForm.Section title={t('basis')}>
          <TypeField />
          <CategoryField />
          <SortOrderField />
          <RequestExcludeField />
          <ColorField />
          <PrivilegedEmailField />
        </MultiStepForm.Section>

        <MultiStepForm.Section title={t('details')}>
          <SubEntitySection />
        </MultiStepForm.Section>
      </Box>
      <MultiStepForm.Actions>
        <LoadingButton
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default CreatePlanUnit;
